import React, { useState } from "react"

import { Brain, ChevronDown, ChevronRight } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import type { ApplicationDetails } from "@/hooks/useApplicationDetails"

interface AssessmentListProps {
  assessments: ApplicationDetails["assessments"]
}

const InfoField = ({
  label,
  value,
  className = "",
}: {
  label: string
  value: string | undefined
  className?: string
}) => (
  <div className={className}>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-medium">{value || "Not provided"}</p>
  </div>
)

// Helper function to format assessment type for display
const formatAssessmentType = (type: string): string => {
  switch (type) {
    case "sjt":
      return "Situational Judgment Test"
    case "orientation-quiz":
      return "Orientation Quiz"
    case "neurapulse":
      return "NeuraPulse Assessment"
    case "driver-onroad-test":
      return "Driver Onroad Test"
    default:
      return type.charAt(0).toUpperCase() + type.slice(1)
  }
}

const AssessmentList = ({ assessments }: AssessmentListProps) => {
  const [isOpen, setIsOpen] = useState(true)

  if (!assessments || assessments.length === 0) {
    return (
      <Card className="shadow-sm">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Brain className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-semibold">Assessments</CardTitle>
                </div>
                {isOpen ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <p className="text-sm text-palette-jungleGreen500">No assessments found</p>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    )
  }

  return (
    <Card className="shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Brain className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">Assessments</CardTitle>
              </div>
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-4">
              {assessments.map((assessment) => (
                <div key={assessment.id} className="border-b">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium">{formatAssessmentType(assessment.type)}</h4>
                    <Badge
                      className={`uppercase ${
                        assessment.result?.toLowerCase() === "pass"
                          ? "bg-green-200 text-green-800 hover:bg-green-300 border-green-300"
                          : assessment.result?.toLowerCase() === "fail"
                            ? "bg-red-200 text-red-800 hover:bg-red-300 border-red-300"
                            : ""
                      }`}
                      variant={
                        assessment.result?.toLowerCase() === "pass"
                          ? "outline"
                          : assessment.result?.toLowerCase() === "fail"
                            ? "outline"
                            : "outline"
                      }
                    >
                      {(assessment.result || "Pending").toUpperCase()}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                    <InfoField
                      label="Score"
                      value={
                        assessment.score !== null ? `${assessment.score.toFixed(2)}` : undefined
                      }
                      className="rounded p-2"
                    />
                    <InfoField
                      label="Completed"
                      value={
                        assessment.completed_at
                          ? new Date(assessment.completed_at).toLocaleDateString()
                          : undefined
                      }
                      className="rounded p-2"
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export default AssessmentList
